import React, { createContext, useState, useEffect, useMemo, useCallback, useContext } from 'react';

type Theme = 'default' | 'green' | 'pink';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const THEME_KEY = 'lifeline_user_theme';

// Create the context with a default value
export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper function to get the initial theme
const getInitialTheme = (): Theme => {
  try {
    const storedTheme = window.localStorage.getItem(THEME_KEY);
    if (storedTheme === 'green' || storedTheme === 'pink' || storedTheme === 'default') {
      return storedTheme;
    }
  } catch (error) {
    console.error("Could not read theme from localStorage", error);
  }
  return 'default'; // Default theme
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, _setTheme] = useState<Theme>(getInitialTheme);

  // Apply the theme class to the root element
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('theme-default', 'theme-green', 'theme-pink');
    root.classList.add(`theme-${theme}`);
  }, [theme]);

  // Memoized setTheme function to prevent unnecessary re-renders
  const setTheme = useCallback((newTheme: Theme) => {
    try {
      window.localStorage.setItem(THEME_KEY, newTheme);
      _setTheme(newTheme);
    } catch (error) {
      console.error("Could not save theme to localStorage", error);
    }
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for easy consumption of the context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};